/**
 * Amoonic Custom JavaScript
 * Ported from old-theme custom.js
 *
 * Features:
 * - Product configurator (metal/stone variant navigation)
 * - Engraving option toggles (Schmuckgravur, Etuigravur, Herz, Plättchen)
 * - Size preservation across variant navigation
 */

window.amoonic = window.amoonic || {};

// Global size tracking for variant navigation
window.amoonic.globalSize = '';
window.amoonic.previewNavigationRequestedAt = 0;

window.amoonic.requestPreviewNavigation = function(delay) {
  window.amoonic.previewNavigationRequestedAt = Date.now();

  setTimeout(function() {
    var galleryEl = document.querySelector('swiper-product-gallery');
    if (!galleryEl || !galleryEl.instance) return;

    var previewSlide = galleryEl.querySelector('.cl-po--preview');
    if (previewSlide) {
      window.amoonic.navigateToPreviewSlide(previewSlide, galleryEl.instance);
    }
  }, delay || 0);
};

window.amoonic.hasPendingPreviewNavigation = function() {
  var requestedAt = window.amoonic.previewNavigationRequestedAt || 0;

  return requestedAt > 0 && Date.now() - requestedAt < 5000;
};

window.amoonic.clearPendingPreviewNavigation = function() {
  window.amoonic.previewNavigationRequestedAt = 0;
};

/**
 * Navigate to a variant product while preserving the selected size
 * @param {string} url - Product URL to navigate to
 */
window.amoonic.navigateToVariant = function(url) {
  document.body.classList.add('loading');
  window.amoonic.rememberConfPosition();
  const sizeParam = window.amoonic.globalSize ? '?size=' + encodeURIComponent(window.amoonic.globalSize) : '';
  window.location.replace(url + sizeParam);
};

/**
 * Configuring a variant means loading another product page. The browser would
 * restore the previous scroll offset, which lands somewhere arbitrary because
 * the target page has a different height. The target page starts at the top.
 */
window.amoonic.CONF_SCROLL_KEY = 'amoonic:confScrollTop';

window.amoonic.rememberConfPosition = function() {
  try {
    history.scrollRestoration = 'manual';
    sessionStorage.setItem(window.amoonic.CONF_SCROLL_KEY, '1');
  } catch (e) {
    // sessionStorage/history can be unavailable - then the browser default applies
  }
};

window.amoonic.restoreConfPosition = function() {
  try {
    const cameFromConfigurator = sessionStorage.getItem(window.amoonic.CONF_SCROLL_KEY);
    sessionStorage.removeItem(window.amoonic.CONF_SCROLL_KEY);
    history.scrollRestoration = 'auto';

    if (cameFromConfigurator) window.scrollTo(0, 0);
  } catch (e) {
    // ignore - without sessionStorage the browser default applies
  }
};

window.amoonic.expandSwatches = function(swatches) {
  if (!swatches) return;
  swatches.classList.add('is-expanded');
  swatches.querySelector('.product-conf__swatches-more')?.setAttribute('aria-expanded', 'true');
};

// Delegated, because the "show all" button can be created later by the merge script
window.amoonic.initSwatchOverflow = function() {
  if (window.amoonic.swatchOverflowDelegated) return;
  window.amoonic.swatchOverflowDelegated = true;

  document.addEventListener('click', function(event) {
    var moreButton = event.target.closest('.product-conf__swatches-more');
    if (!moreButton) return;

    window.amoonic.expandSwatches(moreButton.closest('.product-conf__swatches'));
  });
};

/**
 * Liquid can only iterate 250 products per paginate page. For larger configurator
 * collections the remaining pages are rendered through the Section Rendering API
 * and their options are merged into the groups already on the page.
 */
window.amoonic.confOptionItems = function(container) {
  return Array.from(container.querySelectorAll('[data-value]')).map(function(el) {
    return el.closest('.product-conf__swatch-option') || el;
  });
};

window.amoonic.insertConfGroup = function(root, fetchedGroup) {
  var group = document.importNode(fetchedGroup, true);
  var order = parseInt(group.dataset.confOrder, 10) || 99;
  var reference = Array.from(root.children).find(function(child) {
    return (parseInt(child.dataset.confOrder, 10) || 99) > order;
  });

  root.insertBefore(group, reference || null);
};

window.amoonic.mergeConfGroups = function(root, fetched) {
  fetched.querySelectorAll('[data-conf-group]').forEach(function(fetchedGroup) {
    var liveGroup = root.querySelector('[data-conf-group="' + fetchedGroup.dataset.confGroup + '"]');
    if (!liveGroup) {
      amoonic.insertConfGroup(root, fetchedGroup);
      return;
    }

    var liveOptions = liveGroup.querySelector('[data-conf-options]');
    var fetchedOptions = fetchedGroup.querySelector('[data-conf-options]');
    if (!liveOptions || !fetchedOptions) return;

    var known = {};
    liveOptions.querySelectorAll('[data-value]').forEach(function(el) {
      known[el.dataset.value] = el;
    });

    fetchedOptions.querySelectorAll('[data-value]').forEach(function(el) {
      var knownEl = known[el.dataset.value];
      // A sold-out option is replaced when another page offers it available
      if (knownEl && knownEl.dataset.available === 'false' && el.dataset.available !== 'false') {
        var replacement = document.importNode(el, true);
        replacement.classList.toggle('is-active', knownEl.classList.contains('is-active'));
        knownEl.replaceWith(replacement);
        known[el.dataset.value] = replacement;
        return;
      }
      if (knownEl) return;
      known[el.dataset.value] = el;
      liveOptions.appendChild(document.importNode(el.closest('.product-conf__swatch-option') || el, true));
    });
  });
};

window.amoonic.finalizeConfGroups = function(root) {
  root.querySelectorAll('[data-conf-group]').forEach(function(group) {
    var options = group.querySelector('[data-conf-options]');
    if (!options) return;

    var items = amoonic.confOptionItems(options);
    var isSorted = items.length > 0 && items.every(function(item) { return item.dataset.sortIndex; });
    if (isSorted) {
      items
        .slice()
        .sort(function(a, b) {
          return parseInt(a.dataset.sortIndex, 10) - parseInt(b.dataset.sortIndex, 10);
        })
        .forEach(function(item) {
          options.appendChild(item);
        });
      items = amoonic.confOptionItems(options);
    }

    group.hidden = items.length < 2 && !group.hasAttribute('data-conf-keep-visible');

    var moreButton = options.querySelector('.product-conf__swatches-more');
    var moreLabel = group.dataset.confMoreLabel;
    if (!moreLabel) return;

    var activeIndex = items.findIndex(function(item) {
      return item.classList.contains('is-active') || item.querySelector('.is-active');
    });
    var limit = activeIndex >= 6 ? 5 : 6;
    var hiddenCount = 0;

    items.forEach(function(item, index) {
      var isVisible = index < limit || index === activeIndex;
      item.classList.toggle('product-conf__swatch-option--overflow', !isVisible);
      if (!isVisible) hiddenCount += 1;
    });

    if (hiddenCount === 0) {
      if (moreButton) moreButton.remove();
      return;
    }

    if (!moreButton) {
      moreButton = document.createElement('button');
      moreButton.type = 'button';
      moreButton.className = 'product-conf__swatches-more';
      moreButton.setAttribute('aria-expanded', 'false');
    }
    moreButton.textContent = 'Alle ' + items.length + ' ' + moreLabel + ' anzeigen';
    options.appendChild(moreButton);
  });
};

window.amoonic.initConfMerge = function() {
  var root = document.querySelector('.product-conf[data-conf-pages]');
  if (!root || root.dataset.confMergeStarted === 'true') return;

  var pages = parseInt(root.dataset.confPages, 10) || 1;
  var sectionId = root.dataset.confSection;
  var productUrl = root.dataset.confProductUrl;
  if (pages < 2 || !sectionId || !productUrl) return;

  root.dataset.confMergeStarted = 'true';

  var requests = [];
  for (var page = 2; page <= pages; page++) {
    requests.push(
      fetch(productUrl + '?section_id=' + encodeURIComponent(sectionId) + '&page=' + page)
        .then(function(response) {
          return response.ok ? response.text() : '';
        })
        .catch(function() {
          return '';
        })
    );
  }

  Promise.all(requests).then(function(pageResults) {
    pageResults.forEach(function(html) {
      if (!html) return;
      var fetched = new DOMParser().parseFromString(html, 'text/html').querySelector('.product-conf');
      if (fetched) amoonic.mergeConfGroups(root, fetched);
    });

    amoonic.finalizeConfGroups(root);
    amoonic.initSwatchOverflow();
  });
};

/**
 * Get URL parameters
 * @returns {Object} URL parameters as key-value pairs
 */
window.amoonic.getUrlParams = function() {
  const params = {};
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

/**
 * Close all configurator dropdowns
 */
window.amoonic.closeAllDropdowns = function() {
  document.querySelectorAll('.product-conf__dropdown').forEach(dropdown => {
    dropdown.hidden = true;
    const dropdownId = dropdown.getAttribute('data-conf-dropdown');
    const trigger = document.querySelector(`[data-conf-trigger="${dropdownId}"]`);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  });
};

/**
 * Get the currently open dropdown (if any)
 * @returns {Element|null}
 */
window.amoonic.getOpenDropdown = function() {
  return document.querySelector('.product-conf__dropdown:not([hidden])');
};

/**
 * Initialize product configurator dropdowns
 */
window.amoonic.initConfigurator = function() {
  // Handle dropdown triggers
  document.querySelectorAll('[data-conf-trigger]').forEach(trigger => {
    trigger.addEventListener('click', function(e) {
      e.preventDefault();
      const dropdownId = this.getAttribute('data-conf-trigger');
      const dropdown = document.querySelector(`[data-conf-dropdown="${dropdownId}"]`);

      // Close other dropdowns
      document.querySelectorAll('.product-conf__dropdown').forEach(d => {
        if (d !== dropdown) {
          d.hidden = true;
          const otherTrigger = document.querySelector(`[data-conf-trigger="${d.getAttribute('data-conf-dropdown')}"]`);
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
        }
      });

      // Toggle current dropdown
      const isHidden = dropdown.hidden;
      dropdown.hidden = !isHidden;
      this.setAttribute('aria-expanded', isHidden ? 'true' : 'false');

      // Focus first option when opening
      if (isHidden) {
        const firstOption = dropdown.querySelector('.product-conf__option-btn');
        if (firstOption) firstOption.focus();
      }
    });
  });

  // Handle close buttons
  document.querySelectorAll('[data-conf-close]').forEach(closeBtn => {
    closeBtn.addEventListener('click', function() {
      const dropdown = this.closest('.product-conf__dropdown');
      if (dropdown) {
        dropdown.hidden = true;
        const dropdownId = dropdown.getAttribute('data-conf-dropdown');
        const trigger = document.querySelector(`[data-conf-trigger="${dropdownId}"]`);
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus();
        }
      }
    });
  });

  // Close on outside click
  document.addEventListener('click', function(e) {
    if (!e.target.closest('.product-conf__selector')) {
      window.amoonic.closeAllDropdowns();
    }
  });

  // Keyboard navigation
  document.addEventListener('keydown', function(e) {
    const openDropdown = window.amoonic.getOpenDropdown();

    // Escape closes dropdown
    if (e.key === 'Escape' && openDropdown) {
      e.preventDefault();
      const dropdownId = openDropdown.getAttribute('data-conf-dropdown');
      const trigger = document.querySelector(`[data-conf-trigger="${dropdownId}"]`);
      openDropdown.hidden = true;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
      return;
    }

    // Arrow keys navigate options within open dropdown
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && openDropdown) {
      e.preventDefault();
      const options = Array.from(openDropdown.querySelectorAll('.product-conf__option-btn'));
      if (options.length === 0) return;

      const currentIndex = options.findIndex(opt => opt === document.activeElement);
      let nextIndex;

      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
      }

      options[nextIndex].focus();
      return;
    }

    // Enter or Space activates focused option
    if ((e.key === 'Enter' || e.key === ' ') && openDropdown) {
      const focused = document.activeElement;
      if (focused && focused.classList.contains('product-conf__option-btn')) {
        e.preventDefault();
        focused.click();
      }
    }
  });
};

/**
 * Initialize engraving option toggles
 */
/**
 * Enhance an engraving text input with header row, placeholder & character counter.
 * Wraps the input once; subsequent calls just show/hide the wrapper.
 */
window.amoonic.enhanceEngravingField = function(input) {
  if (!input || input.dataset.enhanced) return;
  input.dataset.enhanced = 'true';

  var maxLen = parseInt(input.getAttribute('maxlength'), 10) || 15;
  input.setAttribute('maxlength', maxLen);
  if (!input.getAttribute('placeholder')) {
    input.placeholder = 'Schreibe den Text hier';
  }

  // Build wrapper
  var wrapper = document.createElement('div');
  wrapper.className = 'engraving-field';

  // Header row
  var header = document.createElement('div');
  header.className = 'engraving-field__header';

  var label = document.createElement('span');
  label.className = 'engraving-field__label';
  // Try to get label text from the original label element
  var origLabel = input.closest('div') ? input.closest('div').querySelector('label') : null;
  label.textContent = origLabel ? origLabel.textContent : 'Anpassen Gravurtext';
  if (origLabel) origLabel.style.display = 'none';

  header.appendChild(label);

  // Input row with counter
  var inputRow = document.createElement('div');
  inputRow.className = 'engraving-field__input-wrap';

  var counter = document.createElement('span');
  counter.className = 'engraving-field__counter';
  counter.textContent = '0/' + maxLen;

  // Insert wrapper into DOM
  input.parentNode.insertBefore(wrapper, input);
  wrapper.appendChild(header);
  inputRow.appendChild(input);
  inputRow.appendChild(counter);
  wrapper.appendChild(inputRow);

  // Update counter on input
  input.addEventListener('input', function() {
    counter.textContent = input.value.length + '/' + maxLen;
  });
};

/**
 * Helper: show an engraving field and its label
 */
window.amoonic.showEngravingField = function(el) {
  if (!el) return;
  amoonic.enhanceEngravingField(el);
  // Show the wrapper if enhanced, otherwise show the element
  var wrapper = el.closest('.engraving-field');
  if (wrapper) {
    wrapper.style.display = 'block';
  } else {
    el.style.display = 'block';
    var label = el.closest('div') ? el.closest('div').querySelector('label') : null;
    if (label) label.style.display = 'block';
  }
};

/**
 * Helper: hide an engraving field and its label, clear value
 */
window.amoonic.hideEngravingField = function(el) {
  if (!el) return;
  var wrapper = el.closest('.engraving-field');
  if (wrapper) {
    wrapper.style.display = 'none';
  } else {
    el.style.display = 'none';
  }
  el.value = '';
  // Update counter if present
  var counter = wrapper ? wrapper.querySelector('.engraving-field__counter') : null;
  if (counter) {
    var maxLen = parseInt(el.getAttribute('maxlength'), 10) || 20;
    counter.textContent = '0/' + maxLen;
  }
};

window.amoonic.initEngravingOptions = function() {
  // Etuigravur (Case engraving)
  document.addEventListener('change', function(e) {
    if (e.target.matches('select[name="properties[Auswahl Etuigravur]"]')) {
      var textInput = document.querySelector('input[name="properties[Text Etuigravur]"]');
      if (e.target.value.match(/Individuell/)) {
        amoonic.showEngravingField(textInput);
      } else {
        amoonic.hideEngravingField(textInput);
      }
    }
  });

  // Schmuckgravur (Jewelry engraving)
  document.addEventListener('change', function(e) {
    if (e.target.matches('.schmuckgravur-select select')) {
      var textInput = document.querySelector('.schmuckgravur-text input');
      if (e.target.value.match(/Individuell/)) {
        amoonic.showEngravingField(textInput);
      } else {
        amoonic.hideEngravingField(textInput);
      }
    }
  });

  // Herz (Heart pendant) - Date or Custom Text
  document.addEventListener('change', function(e) {
    if (e.target.matches('.herz-select select')) {
      var datumInput = document.querySelector('.herz-datum input');
      var individuellInput = document.querySelector('.herz-individuell input');

      if (e.target.value.match(/Datum/)) {
        amoonic.showEngravingField(datumInput);
        amoonic.hideEngravingField(individuellInput);
      } else if (e.target.value.match(/Text/)) {
        amoonic.showEngravingField(individuellInput);
        amoonic.hideEngravingField(datumInput);
      } else {
        amoonic.hideEngravingField(datumInput);
        amoonic.hideEngravingField(individuellInput);
      }
    }
  });

  // Plättchen (Plaque) - Year or Initials
  document.addEventListener('change', function(e) {
    if (e.target.matches('.plaettchen-select select')) {
      var jahreszahlInput = document.querySelector('.plaettchen-jahreszahl input');
      var initialenInput = document.querySelector('.plaettchen-initialen input');

      if (e.target.value.match(/Jahreszahl/)) {
        amoonic.showEngravingField(jahreszahlInput);
        amoonic.hideEngravingField(initialenInput);
      } else if (e.target.value.match(/Initialen/)) {
        amoonic.showEngravingField(initialenInput);
        amoonic.hideEngravingField(jahreszahlInput);
      } else {
        amoonic.hideEngravingField(jahreszahlInput);
        amoonic.hideEngravingField(initialenInput);
      }
    }
  });
};

/**
 * Initialize size selection tracking
 */
/**
 * The size option is identified by its group name, not by the input name:
 * inputs are named "<Optionsname>-<Position>" (e.g. "Größe-1"), the option name
 * only exists on the surrounding fieldset.
 */
window.amoonic.isSizeOptionName = function(name) {
  const handle = (name || '')
    .toLowerCase()
    .replace(/ö/g, 'o')
    .replace(/ä/g, 'a')
    .replace(/ü/g, 'u')
    .replace(/ß/g, 'ss');

  return /(size|grosse|groesse|lange|laenge|umfang)/.test(handle);
};

window.amoonic.findSizeInput = function(value) {
  const groups = Array.from(document.querySelectorAll('[data-option-name]'))
    .filter((group) => window.amoonic.isSizeOptionName(group.dataset.optionName));

  for (const group of groups) {
    const input = Array.from(group.querySelectorAll('input[type="radio"]'))
      .find((candidate) => candidate.value === value);
    if (input) return input;
  }

  return null;
};

window.amoonic.initSizeTracking = function() {
  // Restore size from URL parameter
  const params = window.amoonic.getUrlParams();
  if (params.size) {
    // Try to find the corresponding size swatch
    const sizeInput = window.amoonic.findSizeInput(params.size);
    if (sizeInput) {
      // Size exists for this product - select it
      window.amoonic.globalSize = params.size;
      if (!sizeInput.checked) sizeInput.click();
    } else {
      // Size doesn't exist for this product - clear the URL parameter
      // but keep tracking in case user selects a size
      const url = new URL(window.location);
      url.searchParams.delete('size');
      window.history.replaceState({}, '', url);
      window.amoonic.globalSize = '';
    }
  }

  // Without a URL parameter, carry over whatever size is currently selected
  if (!window.amoonic.globalSize) {
    const checked = Array.from(document.querySelectorAll('[data-option-name] input[type="radio"]:checked'))
      .find((input) => window.amoonic.isSizeOptionName(input.closest('[data-option-name]').dataset.optionName));
    if (checked) window.amoonic.globalSize = checked.value;
  }

  // Track size selection changes so navigateToVariant can carry them over
  document.addEventListener('change', function(e) {
    const input = e.target;
    if (!input || !input.matches || !input.matches('input[type="radio"]')) return;

    const group = input.closest('[data-option-name]');
    if (!group || !window.amoonic.isSizeOptionName(group.dataset.optionName)) return;

    window.amoonic.globalSize = input.value;
  });
};

/**
 * Check whether Infinite Options has injected meaningful engraving controls.
 * The app may insert helper markup or hidden inputs, so only real option fields count.
 */
window.amoonic.hasEngravingOptions = function(container) {
  if (!container) return false;

  return Array.from(container.querySelectorAll('input, select, textarea')).some(function(field) {
    return field.type !== 'hidden';
  });
};

/**
 * Keep the custom engraving button hidden unless Infinite Options renders options.
 */
window.amoonic.updateEngravingSectionVisibility = function(container) {
  if (!container) return;

  var section = container.closest('.product-conf__engraving-section');
  if (!section) return;

  var hasOptions = amoonic.hasEngravingOptions(container);

  section.hidden = !hasOptions;
  container.classList.toggle('is-hidden', !hasOptions);
};

/**
 * Infinite Options renders asynchronously, so watch the target container.
 */
window.amoonic.initEngravingSectionVisibility = function() {
  document.querySelectorAll('#infiniteoptions-container').forEach(function(container) {
    if (container.dataset.amoonicEngravingInit === 'true') return;
    container.dataset.amoonicEngravingInit = 'true';

    amoonic.updateEngravingSectionVisibility(container);

    var attempts = 0;
    var maxAttempts = 40;
    var intervalId = window.setInterval(function() {
      attempts += 1;
      amoonic.updateEngravingSectionVisibility(container);

      if (amoonic.hasEngravingOptions(container) || attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, 250);
  });
};

/**
 * Legacy getUrlVars function (compatibility with old theme)
 * @returns {Object} URL parameters as key-value pairs
 */
function getUrlVars() {
  var vars = {};
  window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, function(m, key, value) {
    vars[key] = decodeURIComponent(value);
  });
  return vars;
}

// Make it globally available
window.getUrlVars = getUrlVars;

/**
 * Christmas/Seasonal Delivery Logic
 * Calculates and displays "Lieferung vor Weihnachten" message based on date and delivery time
 */
window.amoonic.christmasDeliveryDates = {
  // Format: 'DD.MM.': maxDeliveryDays
  '10.12.': 5,
  '11.12.': 5,
  '12.12.': 5,
  '13.12.': 5,
  '14.12.': 5,
  '15.12.': 5,
  '16.12.': 3,
  '17.12.': 3,
  '18.12.': 3,
  '19.12.': 1,
  '20.12.': 1,
  '21.12.': 1,
  '22.12.': 1,
  '23.12.': 1  // UPS Express only
};

/**
 * Get current date in DD.MM. format
 * @returns {string} Date string
 */
window.amoonic.getCurrentDateString = function() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return day + '.' + month + '.';
};

/**
 * Check if delivery before Christmas is possible
 * Uses configurable settings from theme if available, falls back to hardcoded dates
 * @param {number} deliveryDays - Number of delivery days
 * @returns {Object|null} Christmas message info or null
 */
window.amoonic.getChristmasDeliveryInfo = function(deliveryDays) {
  // Check if Christmas delivery is enabled via settings
  const settings = window.amoonic.christmasSettings;
  if (settings && settings.enabled === false) {
    return null;
  }

  const now = new Date();
  const day = now.getDate();
  const month = now.getMonth() + 1; // 0-indexed

  // Only check in December
  if (month !== 12) {
    return null;
  }

  // Use configurable settings if available, otherwise use defaults
  const phases = settings && settings.phases ? settings.phases : {
    phase1: { start: 10, end: 15, maxDays: 5 },
    phase2: { start: 16, end: 18, maxDays: 3 },
    phase3: { start: 19, end: 22, maxDays: 1 },
    express: { day: 23, maxDays: 1 }
  };

  const message = settings && settings.message ? settings.message : 'Lieferung noch vor Weihnachten';
  const expressMessage = settings && settings.expressMessage ? settings.expressMessage : 'Lieferung mit UPS EXPRESS vor Weihnachten (bei Bestellung bis 14:00 Uhr)';

  // Phase 1: Dec 10-15 (5 days max)
  if (day >= phases.phase1.start && day <= phases.phase1.end) {
    if (deliveryDays <= phases.phase1.maxDays) {
      return { message: message, isExpress: false };
    }
  }
  // Phase 2: Dec 16-18 (3 days max)
  else if (day >= phases.phase2.start && day <= phases.phase2.end) {
    if (deliveryDays <= phases.phase2.maxDays) {
      return { message: message, isExpress: false };
    }
  }
  // Phase 3: Dec 19-22 (1 day max)
  else if (day >= phases.phase3.start && day <= phases.phase3.end) {
    if (deliveryDays <= phases.phase3.maxDays) {
      return { message: message, isExpress: false };
    }
  }
  // Express: Dec 23 (UPS Express only)
  else if (day === phases.express.day) {
    if (deliveryDays <= phases.express.maxDays) {
      return { message: expressMessage, isExpress: true };
    }
  }

  return null;
};

/**
 * Update delivery time display on PDP
 * @param {number} deliveryDays - Number of delivery days
 */
window.amoonic.updateDeliveryTimeDisplay = function(deliveryDays) {
  const deliveryElement = document.querySelector('.product-conf__delivery-time');
  if (!deliveryElement) return;

  const deliveryText = deliveryElement.querySelector('.product-conf__delivery-days');
  const christmasText = deliveryElement.querySelector('.product-conf__christmas-message');

  if (deliveryText) {
    deliveryText.textContent = deliveryDays + ' Werktag' + (deliveryDays !== 1 ? '(e)' : '');
  }

  if (christmasText) {
    const christmasInfo = window.amoonic.getChristmasDeliveryInfo(deliveryDays);
    if (christmasInfo) {
      christmasText.textContent = christmasInfo.message;
      christmasText.style.display = '';
      christmasText.classList.toggle('express', christmasInfo.isExpress);
    } else {
      christmasText.style.display = 'none';
    }
  }
};

/**
 * Initialize delivery time from data attributes
 */
window.amoonic.initDeliveryTime = function() {
  const deliveryElement = document.querySelector('[data-delivery-time]');
  if (deliveryElement) {
    const deliveryTime = parseInt(deliveryElement.getAttribute('data-delivery-time'), 10);
    if (!isNaN(deliveryTime)) {
      window.amoonic.updateDeliveryTimeDisplay(deliveryTime);
    }
  }
};

/**
 * Medaillon Photo Preview - Fix Swiper interaction & auto-navigate to preview
 * The Live Product Options (Cloudlift) app renders a Fabric.js canvas preview
 * inside a Swiper slide. Swiper captures touch/mouse events preventing
 * image resize/drag on the canvas. This fixes that and auto-navigates to
 * the preview slide when an image is uploaded.
 */
window.amoonic.initMedaillonPreview = function() {
  var galleryEl = document.querySelector('swiper-product-gallery');
  if (!galleryEl) return;
  if (galleryEl.dataset.amoonicPreviewInitialized === 'true') return;
  galleryEl.dataset.amoonicPreviewInitialized = 'true';

  // Wait for Swiper to initialize
  var checkInterval = setInterval(function() {
    if (!galleryEl.instance) return;
    clearInterval(checkInterval);

    var swiper = galleryEl.instance;

    // Watch for the Cloudlift preview canvas being added to the gallery
    var observer = new MutationObserver(function(mutations) {
      amoonic.lockCloudliftGallery();
      amoonic.markPhotoPreviewPage();
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;

          // Detect Cloudlift preview canvas or container
          var canvases = [];
          if (node.tagName === 'CANVAS') {
            canvases.push(node);
          } else if (node.querySelectorAll) {
            canvases = Array.from(node.querySelectorAll('canvas, .cl-po--preview canvas'));
          }

          canvases.forEach(function(canvas) {
            amoonic.setupCanvasInteraction(canvas, swiper);
          });

          // Auto-navigate only after a shopper upload/library action.
          var previewEl = node.classList && node.classList.contains('cl-po--preview')
            ? node
            : (node.querySelector ? node.querySelector('.cl-po--preview') : null);
          if (previewEl && amoonic.hasPendingPreviewNavigation()) {
            amoonic.navigateToPreviewSlide(previewEl, swiper);
          }
        });
      });
    });

    // Observe the entire gallery for new slides/canvases
    observer.observe(galleryEl, { childList: true, subtree: true });

    // Also observe the main media container (preview might be added outside swiper)
    var mediaContainer = document.querySelector('.main-product__media');
    if (mediaContainer && mediaContainer !== galleryEl) {
      observer.observe(mediaContainer, { childList: true, subtree: true });
    }

    // Handle canvases that are already present
    var existingCanvases = galleryEl.querySelectorAll('.cl-po--preview canvas, .upper-canvas');
    existingCanvases.forEach(function(canvas) {
      amoonic.setupCanvasInteraction(canvas, swiper);
    });
    amoonic.lockCloudliftGallery();
    amoonic.markPhotoPreviewPage();
  }, 200);

  // Clear check after 15s to prevent infinite polling
  setTimeout(function() { clearInterval(checkInterval); }, 15000);
};

/**
 * Cloudlift renders its own Splide gallery with all product images into the
 * first gallery slide. A swipe there moved that inner gallery instead of ours:
 * the second image showed twice, and swiping back the first slide showed
 * whichever image the inner gallery had been left on. Keep the inner gallery
 * on its preview slides and leave navigation to the theme gallery, the same
 * thing the app does with its "hideThumbnails" setting (off in all option sets).
 */
window.amoonic.lockCloudliftGallery = function() {
  var app = window.Cloudlift && Cloudlift.options && Cloudlift.options.App;
  var products = app && app.products;
  if (!products) return;

  Object.keys(products).forEach(function(key) {
    var gallery = products[key] && products[key]._gallery;
    var splide = gallery && gallery._splideMain;
    if (!splide || !splide.root || !splide.root.closest('swiper-product-gallery')) return;

    if (!splide._amoonicLocked) {
      splide._amoonicLocked = true;
      splide.Components.Drag.disable(true);
      splide.on('moved', function() { snapToPreview(splide); });
    }
    snapToPreview(splide);
  });

  function snapToPreview(splide) {
    var slides = Array.from(splide.root.querySelectorAll('.splide__list > .splide__slide'));
    var current = slides[splide.index];
    if (!current || current.querySelector('.cl-po--preview')) return;
    var preview = slides.findIndex(function(slide) {
      return slide.querySelector('.cl-po--preview');
    });
    if (preview >= 0) splide.go(preview);
  }
};

/**
 * Pages with a Cloudlift photo preview keep pull-to-refresh disabled
 * (amoonic-photo-preview in amoonic-custom.css): an accidental reload would
 * throw away the shopper's uploaded photos.
 */
window.amoonic.markPhotoPreviewPage = function() {
  if (document.documentElement.classList.contains('amoonic-photo-preview')) return;
  if (document.querySelector('swiper-product-gallery .cl-po--preview')) {
    document.documentElement.classList.add('amoonic-photo-preview');
  }
};

/**
 * Set up canvas interaction - disable Swiper when interacting with Fabric.js canvas
 * Only targets canvases inside the product gallery's .cl-po--preview
 */
window.amoonic.setupCanvasInteraction = function(canvas, swiper) {
  if (!canvas || canvas.dataset.amonicInteractionSetup) return;
  // Only set up on canvases inside a preview container
  if (!canvas.closest('.cl-po--preview')) return;
  canvas.dataset.amonicInteractionSetup = 'true';

  // Stop the event from bubbling up to Swiper and keep Swiper still
  // This lets Fabric.js handle drag/resize while Swiper ignores the gesture
  function lockSwiper(e) {
    e.stopPropagation();
    swiper.allowTouchMove = false;
  }

  // Mouse: every drag on the canvas belongs to Fabric
  canvas.addEventListener('pointerdown', function(e) {
    if (e.pointerType === 'mouse') lockSwiper(e);
  });
  canvas.addEventListener('mousedown', lockSwiper);

  // Touch: Swiper has already seen the pointerdown. Fabric's touchstart
  // listener (registered before ours) has hit-tested by now, so only a touch
  // on a layer (the uploaded photo) or a pinch is kept from Swiper; any other
  // swipe over the preview changes slides like on the other images.
  canvas.addEventListener('touchstart', function(e) {
    if (e.touches.length > 1 || (amoonic.isPreviewLayerTouch && amoonic.isPreviewLayerTouch(canvas))) {
      lockSwiper(e);
    }
  });

  // Re-enable Swiper when interaction ends (scoped to canvas, not window)
  ['pointerup', 'mouseup', 'touchend', 'mouseleave'].forEach(function(eventName) {
    canvas.addEventListener(eventName, function() {
      setTimeout(function() { swiper.allowTouchMove = true; }, 150);
    });
  });
};

/**
 * The Cloudlift preview canvas has touch-action: none (Fabric.js plus our
 * gallery CSS), so on phones a swipe across the main image never scrolls the
 * page. Fabric only needs the gesture when it starts on a layer (the uploaded
 * photo); everywhere else we scroll the page ourselves.
 */
window.amoonic.initPreviewCanvasScroll = function() {
  if (window.amoonic._previewCanvasScrollInitialized) return;
  window.amoonic._previewCanvasScrollInitialized = true;

  var CANVAS_SELECTOR = '.cl-po--preview canvas.upper-canvas';
  var LOCK_DISTANCE = 6;
  var gesture = null;
  var momentumFrame = null;

  // Fabric 4 keeps no reference from the element to its instance, so record
  // what findTarget returns while Fabric handles the touchstart.
  function patchFabric() {
    var proto = window.fabric && window.fabric.Canvas && window.fabric.Canvas.prototype;
    if (!proto || proto._amoonicFindTargetPatched) return;
    var findTarget = proto.findTarget;
    proto.findTarget = function() {
      var target = findTarget.apply(this, arguments);
      if (gesture && gesture.canvas === this.upperCanvasEl && !gesture.checked) {
        gesture.onLayer = gesture.onLayer || !!target;
      }
      return target;
    };
    proto._amoonicFindTargetPatched = true;
  }

  // Valid during the touchstart, after Fabric's listener has run.
  window.amoonic.isPreviewLayerTouch = function(canvas) {
    return !!(gesture && gesture.canvas === canvas && gesture.onLayer);
  };

  function stopMomentum() {
    if (momentumFrame) cancelAnimationFrame(momentumFrame);
    momentumFrame = null;
  }

  function startMomentum(velocity) {
    var last = performance.now();
    function step(now) {
      var dt = now - last;
      last = now;
      velocity *= Math.pow(0.95, dt / 16);
      if (Math.abs(velocity) < 0.02) { momentumFrame = null; return; }
      window.scrollBy(0, velocity * dt);
      momentumFrame = requestAnimationFrame(step);
    }
    momentumFrame = requestAnimationFrame(step);
  }

  // Capture phase: runs before Fabric's own listener on the canvas.
  document.addEventListener('touchstart', function(e) {
    stopMomentum();
    var canvas = e.target.closest && e.target.closest(CANVAS_SELECTOR);
    if (!canvas || e.touches.length !== 1) { gesture = null; return; }
    patchFabric();
    var touch = e.touches[0];
    gesture = {
      canvas: canvas,
      onLayer: false,
      checked: false,
      axis: null,
      startX: touch.clientX,
      startY: touch.clientY,
      lastY: touch.clientY,
      lastTime: e.timeStamp,
      velocity: 0
    };
  }, { capture: true, passive: true });

  document.addEventListener('touchmove', function(e) {
    if (!gesture) return;
    gesture.checked = true;
    if (gesture.onLayer || e.touches.length !== 1) { gesture = null; return; }

    var touch = e.touches[0];
    if (!gesture.axis) {
      var dx = Math.abs(touch.clientX - gesture.startX);
      var dy = Math.abs(touch.clientY - gesture.startY);
      if (dx < LOCK_DISTANCE && dy < LOCK_DISTANCE) return;
      gesture.axis = dy >= dx ? 'y' : 'x';
    }
    if (gesture.axis !== 'y') return;

    var delta = gesture.lastY - touch.clientY;
    var dt = Math.max(1, e.timeStamp - gesture.lastTime);
    window.scrollBy(0, delta);
    gesture.velocity = 0.8 * (delta / dt) + 0.2 * gesture.velocity;
    gesture.lastY = touch.clientY;
    gesture.lastTime = e.timeStamp;
  }, { capture: true, passive: true });

  ['touchend', 'touchcancel'].forEach(function(eventName) {
    document.addEventListener(eventName, function(e) {
      if (!gesture) return;
      if (gesture.axis === 'y' && e.timeStamp - gesture.lastTime < 100) {
        startMomentum(gesture.velocity);
      }
      gesture = null;
    }, { capture: true, passive: true });
  });
};

/**
 * Navigate the gallery Swiper to the slide containing the preview
 */
window.amoonic.navigateToPreviewSlide = function(previewEl, swiper) {
  if (!amoonic.hasPendingPreviewNavigation()) return;

  // Find the swiper slide that contains this preview
  var slide = previewEl.closest('.swiper-slide');
  if (slide) {
    var index = Array.from(slide.parentElement.children).indexOf(slide);
    if (index >= 0 && swiper.slideTo) {
      if (swiper.activeIndex !== index) {
        swiper.slideTo(index, 0);
      }
      amoonic.clearPendingPreviewNavigation();
    }
  }
};

/**
 * Watch for image uploads in the Live Product Options form and auto-navigate
 */
window.amoonic.watchForImageUploads = function() {
  if (window.amoonic._imageUploadWatcherInitialized) return;
  window.amoonic._imageUploadWatcherInitialized = true;

  // Watch for file input changes in the Cloudlift options form
  document.addEventListener('change', function(e) {
    if (e.target.type === 'file' && e.target.closest('[data-live-options], .cl-po--upload, #infiniteoptions-container')) {
      // Wait for the preview to render after upload
      amoonic.requestPreviewNavigation(1500);
    }
  });

  // Also watch for Cloudlift's custom events (upload-lift)
  document.addEventListener('click', function(e) {
    var uploadBtn = e.target.closest('.cl-po--upload-button, .cl-po--library-images button');
    if (!uploadBtn) return;

    amoonic.requestPreviewNavigation(2000);
  });
};

/**
 * Group Cloudlift's two medaillon photo upload options in one row.
 * The app injects these fields after page load, so the wrapper has to be
 * applied in the browser instead of Liquid.
 */
window.amoonic.initMedaillonUploadGroup = function() {
  if (window.amoonic._medaillonUploadGroupInitialized) return;
  window.amoonic._medaillonUploadGroupInitialized = true;

  function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function getUploadOptions(root) {
    var scope = root && root.querySelectorAll ? root : document;
    return Array.from(scope.querySelectorAll('.cl-po--option')).filter(function(option) {
      if (!option.querySelector('.cl-po--upload')) return false;
      // The app can also render the uploads inside its own popup, which brings
      // its own layout - moving those out would empty the popup.
      return !option.closest('.cl-po--popup, .ldcv');
    });
  }

  function setupUploadCard(option, index, total) {
    if (option.querySelector('.amoonic-medaillon-upload-copy')) return;

    var upload = option.querySelector('.cl-po--upload');
    if (!upload) return;
    option.classList.add('amoonic-medaillon-upload-card');
    option.setAttribute('role', 'button');
    option.setAttribute('tabindex', '0');

    // The app's own "Bild links"/"Bild rechts" label tells us which side
    // this field is for; its wording isn't shown, we just render our own
    // pill text ("Upload Foto links"/"Upload Foto rechts") from it.
    var title = Array.from(option.querySelectorAll('label, .cl-po--label, [class*="label"], [class*="title"]')).find(function(element) {
      var text = normalizeText(element.textContent);
      return /^bild\s+(links|rechts)$/i.test(text);
    });

    // Products with only one photo field (e.g. single-side medaillons) have
    // no "left/right" pair, so no side suffix should be shown - even if the
    // app's own field happens to be internally named/labelled "Bild links".
    var side = total === 1 ? '' : (index === 1 ? 'rechts' : 'links');
    if (title) {
      if (total !== 1) {
        var match = normalizeText(title.textContent).match(/^bild\s+(links|rechts)$/i);
        if (match) side = match[1].toLowerCase();
      }
      title.remove();
    }

    var help = Array.from(option.querySelectorAll('div, p, span, small')).find(function(element) {
      if (element.closest('.cl-po--upload')) return false;
      if (element.querySelector('.cl-po--upload')) return false;
      var text = normalizeText(element.textContent);
      return /hier hochladen/i.test(text);
    });
    if (help) help.remove();

    var copy = document.createElement('div');
    copy.className = 'amoonic-medaillon-upload-copy';

    var titleEl = document.createElement('div');
    titleEl.className = 'amoonic-medaillon-upload-title';
    titleEl.textContent = side ? 'Upload Foto ' + side : 'Upload Foto';
    copy.appendChild(titleEl);

    upload.insertAdjacentElement('afterend', copy);

    option.addEventListener('click', function(event) {
      if (event.target.closest('.cl-po--upload, input, button, a, select, textarea')) return;

      var uploadButton = option.querySelector('.cl-po--upload-button');
      if (uploadButton) uploadButton.click();
    });

    option.addEventListener('keydown', function(event) {
      if (event.target !== option) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;

      var uploadButton = option.querySelector('.cl-po--upload-button');
      if (!uploadButton) return;

      event.preventDefault();
      uploadButton.click();
    });
  }

  function groupUploads() {
    // Group per container. Taking the first two uploads found anywhere would
    // move them out of the app's own group, which the app later relocates into
    // its popup - the popup then ends up without its upload fields.
    var byParent = new Map();

    getUploadOptions(document).forEach(function(option) {
      var parent = option.closest('.amoonic-medaillon-upload-group') || option.parentNode;
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent).push(option);
    });

    byParent.forEach(function(options, parent) {
      options.forEach(function(option, index) {
        setupUploadCard(option, index, options.length);
      });

      var alreadyGrouped = parent.classList && parent.classList.contains('amoonic-medaillon-upload-group');
      if (alreadyGrouped || options.length < 2) return;

      var pair = options.slice(0, 2);
      var wrapper = document.createElement('div');
      wrapper.className = 'amoonic-medaillon-upload-group';
      pair[0].parentNode.insertBefore(wrapper, pair[0]);
      pair.forEach(function(option) {
        wrapper.appendChild(option);
      });
    });
  }

  groupUploads();

  window.amoonic._medaillonUploadGroupObserver = new MutationObserver(function(mutations) {
    var shouldGroup = mutations.some(function(mutation) {
      return Array.from(mutation.addedNodes).some(function(node) {
        return node.nodeType === 1 && (
          node.matches && node.matches('.cl-po--option, .cl-po--upload') ||
          node.querySelector && node.querySelector('.cl-po--option, .cl-po--upload')
        );
      });
    });

    if (shouldGroup) groupUploads();
  });

  window.amoonic._medaillonUploadGroupObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
};

/**
 * Cloudlift lays its option cards (Fotoservice checkbox, Schmuckverpackung,
 * Geschenkverpackung, ...) out as a flex column spaced with margin, not a
 * flex gap - inconsistently (10-20px depending on the card), and at least
 * one card ("Geschenkverpackung") carries its own margin-top that beats a
 * plain CSS override regardless of load order (its origin/specificity isn't
 * inspectable - the stylesheet is cross-origin). Setting the inline style
 * property with 'important' priority is the only override that reliably
 * wins. "Schmuckverpackung"/"Geschenkverpackung" also get no divider of
 * their own, so mark them for the same section-divider styling as the
 * theme's own product-conf groups (Material, Veredelung, ...).
 */
window.amoonic.initFeatureSectionDividers = function() {
  if (window.amoonic._featureSectionDividerObserver) return;
  window.amoonic._featureSectionDividerObserver = true;

  var DIVIDER_CLASS = 'amoonic-feature-section-divider';
  var TITLE_PATTERN = /^(schmuckverpackung|geschenkverpackung)\b/i;

  function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function normalizeSpacing(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.from(scope.querySelectorAll('.cl-po--options')).forEach(function(optionsList) {
      var children = Array.from(optionsList.children).filter(function(child) {
        return child.matches('.cl-po--option, .amoonic-medaillon-upload-group');
      });

      children.forEach(function(child, index) {
        child.style.setProperty('margin-top', '0', 'important');
        var isLast = index === children.length - 1;
        child.style.setProperty('margin-bottom', isLast ? '0' : '', isLast ? 'important' : '');

        // Skip the divider on the first card - the surrounding
        // .product__app-block already draws a border above the whole list,
        // so marking the first card too would double that line (seen e.g.
        // on products without a Fotoservice checkbox, where "Schmuckverpackung"
        // is the first and only card).
        if (index === 0) {
          child.classList.remove(DIVIDER_CLASS);
          return;
        }

        var label = child.querySelector('.cl-po--label');
        if (label && TITLE_PATTERN.test(normalizeText(label.textContent))) {
          child.classList.add(DIVIDER_CLASS);
        }
      });
    });
  }

  normalizeSpacing(document);

  function touchesOptionList(nodes) {
    return Array.from(nodes).some(function(node) {
      return node.nodeType === 1 && (
        node.matches && node.matches('.cl-po--option') ||
        node.querySelector && node.querySelector('.cl-po--option')
      );
    });
  }

  var observer = new MutationObserver(function(mutations) {
    // Cloudlift sometimes renders a transient option (e.g. a Fotoservice
    // placeholder) and removes it again once it determines it doesn't apply
    // to this product. Watching only additions would miss that: the
    // remaining cards keep whatever index-based margin/divider class they
    // got while the placeholder was still there.
    var shouldScan = mutations.some(function(mutation) {
      return touchesOptionList(mutation.addedNodes) || touchesOptionList(mutation.removedNodes);
    });

    if (shouldScan) normalizeSpacing(document);
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Safety net: the observer reacts to DOM changes, but Cloudlift can also
  // restyle/reorder cards without adding or removing any node (e.g. toggling
  // a disabled state), which wouldn't trigger it at all. Re-run for a few
  // seconds after load to catch that too - same pattern as
  // initEngravingSectionVisibility above, for the same reason.
  var attempts = 0;
  var maxAttempts = 40;
  var intervalId = window.setInterval(function() {
    attempts += 1;
    normalizeSpacing(document);
    if (attempts >= maxAttempts) window.clearInterval(intervalId);
  }, 250);
};

/**
 * Require at least one uploaded image when the shopper chooses the paid
 * medaillon photo service.
 */
window.amoonic.initMedaillonUploadValidation = function() {
  if (window.amoonic._medaillonUploadValidationInitialized) return;
  window.amoonic._medaillonUploadValidationInitialized = true;
  
  var ERROR_MESSAGE = 'Bitte laden Sie ein Foto hoch, wenn Sie den Fotoservice auswählen.';

  function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function isVisible(element) {
    if (!element) return false;
    if (element.hidden) return false;
    var style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }

  function selectedOptionText(input) {
    var idLabel = input.id ? document.querySelector('label[for="' + CSS.escape(input.id) + '"]') : null;
    var wrapper = input.closest('label, .cl-po--option, .cl-po--value, .cl-po--checkbox, .cl-po--radio, div');
    return normalizeText((idLabel ? idLabel.textContent + ' ' : '') + (wrapper ? wrapper.textContent : input.value));
  }

  function hasFotoserviceSelected() {
    var checkedInputs = Array.from(document.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked'));
    var checkedFotoservice = checkedInputs.some(function(input) {
      var text = selectedOptionText(input);
      return /mit\s+fotoservice/i.test(text) && !/ohne\s+fotos/i.test(text);
    });

    if (checkedFotoservice) return true;

    var selectedCustomOption = Array.from(document.querySelectorAll('[aria-checked="true"], .is-selected, .is-active, .selected')).some(function(element) {
      var text = normalizeText(element.textContent);
      return /mit\s+fotoservice/i.test(text) && !/ohne\s+fotos/i.test(text);
    });

    if (selectedCustomOption) return true;

    var uploadGroup = document.querySelector('.amoonic-medaillon-upload-group');
    return uploadGroup && isVisible(uploadGroup);
  }

  function uploadOptionHasImage(option) {
    var fileInput = option.querySelector('input[type="file"]');
    if (fileInput && fileInput.files && fileInput.files.length > 0) return true;

    var valuedInput = Array.from(option.querySelectorAll('input, textarea')).some(function(input) {
      var value = normalizeText(input.value);
      return /uploads|cdn|blob:|data:image|\.jpe?g|\.png|\.webp|\.gif/i.test(value);
    });
    if (valuedInput) return true;

    return Array.from(option.querySelectorAll('img[src]')).some(function(img) {
      var src = normalizeText(img.getAttribute('src'));
      return src && !/placeholder|blank|spacer/i.test(src);
    });
  }

  function hasRequiredUploadedImages() {
    return Array.from(
      document.querySelectorAll('.cl-po--option')
    ).some(function(option) {
      return option.querySelector('.cl-po--upload') &&
            uploadOptionHasImage(option);
    });
  }

  function getProductForm() {
    return document.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
  }

  function showError(form) {
    var wrapper = form && form.querySelector('.product-form__error-message-wrapper');
    var message = wrapper && wrapper.querySelector('.product-form__error-message');

    if (message) message.textContent = ERROR_MESSAGE;
    if (wrapper) wrapper.hidden = false;

    var uploadGroup = document.querySelector('.amoonic-medaillon-upload-group');
    if (uploadGroup) {
      uploadGroup.classList.add('amoonic-medaillon-upload-group--error');
      uploadGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function clearError(form) {
    var wrapper = form && form.querySelector('.product-form__error-message-wrapper');
    if (wrapper) wrapper.hidden = true;

    var uploadGroup = document.querySelector('.amoonic-medaillon-upload-group');
    if (uploadGroup) uploadGroup.classList.remove('amoonic-medaillon-upload-group--error');
  }

  function validateMedaillonUpload(form) {
    if (!hasFotoserviceSelected()) {
      clearError(form);
      return true;
    }

    if (hasRequiredUploadedImages()) {
      clearError(form);
      return true;
    }

    showError(form);
    return false;
  }

  document.addEventListener('submit', function(event) {
    var form = event.target && event.target.closest ? event.target.closest('form[data-type="add-to-cart-form"], form[action*="/cart/add"]') : null;
    if (!form) return;

    if (!validateMedaillonUpload(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('click', function(event) {
    var button = event.target && event.target.closest ? event.target.closest('button[name="add"], .product-bar__submit') : null;
    if (!button) return;

    var formId = button.getAttribute('form');
    var form = formId ? document.getElementById(formId) : button.closest('form');
    form = form || getProductForm();

    if (form && !validateMedaillonUpload(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
};

/**
 * Cloudlift injects packaging hover cards with app-owned markup.
 * Tag only those injected cards so the theme can override the harsh default UI.
 */
window.amoonic.initPackagingPopoverCards = function() {
  if (window.amoonic._packagingPopoverObserver) return;

  var CARD_CLASS = 'amoonic-packaging-popover';
  var TITLE_CLASS = 'amoonic-packaging-popover__title';
  var PACKAGING_TEXT = /(geschenkset|geschenkt(?:ü|ue)te|geschenkbeutel|geschenkbox|geschenkschleife|geschenkanh|schmucketui|umkarton|jewelry packaging|jewelry case|gift set|gift bag|gift box|case\s*\(|verpackung|clutch|ihre gravur)/i;
  var TITLE_TEXT = /(geschenkset|geschenkt(?:ü|ue)te|geschenkbeutel|geschenkbox|case|gift set|gift bag|gift box|jewelry packaging|jewelry case|verpackung).{0,60}(\d+[,.]\d{2}\s*€|€|\))/i;

  function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function ownText(element) {
    return normalizeText(Array.from(element.childNodes).map(function(node) {
      return node.nodeType === Node.TEXT_NODE ? node.textContent : '';
    }).join(' '));
  }

  function isPackagingCard(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.classList.contains(CARD_CLASS)) return true;
    if (!element.querySelector('img')) return false;

    var text = normalizeText(element.innerText || element.textContent);
    if (text.length < 20 || text.length > 900 || !PACKAGING_TEXT.test(text)) return false;
    if (element.matches('[data-live-options], form, .product__info, .main-product__media, .cl-po--option')) return false;

    var style = window.getComputedStyle(element);
    var zIndex = parseInt(style.zIndex, 10);
    return style.position === 'absolute' || style.position === 'fixed' || zIndex > 10;
  }

  function positionedPackagingAncestors(element) {
    var ancestors = [];
    var parent = element.parentElement;

    while (parent && parent !== document.body) {
      if (parent.matches('[data-live-options], form, .product__info, .main-product__media, .main-product')) break;

      var text = normalizeText(parent.innerText || parent.textContent);
      var style = window.getComputedStyle(parent);
      var zIndex = parseInt(style.zIndex, 10);
      var isOverlay = style.position === 'absolute' || style.position === 'fixed' || zIndex > 10;

      if (isOverlay && parent.querySelector('img') && PACKAGING_TEXT.test(text)) {
        ancestors.push(parent);
      }

      parent = parent.parentElement;
    }

    return ancestors;
  }

  function markTitle(card) {
    if (card.querySelector('.' + TITLE_CLASS)) return;

    var textElements = Array.from(card.querySelectorAll('div, p, span, h1, h2, h3, h4, h5, h6')).filter(function(element) {
      if (element.querySelector('img')) return false;
      var text = ownText(element) || normalizeText(element.textContent);
      return text.length > 0 && text.length <= 120;
    });

    var title = textElements.find(function(element) {
      var text = ownText(element) || normalizeText(element.textContent);
      return TITLE_TEXT.test(text);
    }) || textElements[0];

    if (title) title.classList.add(TITLE_CLASS);
  }

  function scan(root) {
    if (!(root instanceof HTMLElement)) return;

    var candidates = [root].concat(Array.from(root.querySelectorAll(
      '[role="tooltip"], [class*="tooltip"], [class*="popover"], [class*="card"], [class*="description"], [class*="info"], [class*="help"], div'
    )));

    candidates
      .filter(isPackagingCard)
      .sort(function(a, b) {
        return normalizeText(a.innerText || a.textContent).length - normalizeText(b.innerText || b.textContent).length;
      })
      .slice(0, 3)
      .forEach(function(card) {
        [card].concat(positionedPackagingAncestors(card)).forEach(function(target) {
          target.classList.add(CARD_CLASS);
          markTitle(target);
        });
      });
  }

  scan(document.body);

  window.amoonic._packagingPopoverObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(scan);
      if (mutation.target instanceof HTMLElement) scan(mutation.target);
      if (mutation.target.nodeType === Node.TEXT_NODE && mutation.target.parentElement) scan(mutation.target.parentElement);
    });
  });

  window.amoonic._packagingPopoverObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });
};

/**
 * Card variant swatches (metal + stone) - hover preview, click navigate
 * Hover: swaps the first slide image src using data-product-image (no swiper rebuild).
 * Mouseleave: restores original src.
 * Click: navigates to the variant product URL.
 */
window.amoonic.initCardMetalSwatches = function() {
  var SWATCH_SELECTOR = '.card-metal-swatches__swatch, .card-stone-swatches__swatch';
  var CONTAINER_SELECTOR = '.card-metal-swatches, .card-stone-swatches';

  function getProductCard(el) {
    return el.closest('product-card') || el.closest('.product-card');
  }

  function getFirstImg(productCard) {
    return productCard.querySelector(
      'product-card-media-slider .swiper-slide:first-child img, .product-card__media img'
    );
  }

  function getMaterialLabel(productCard) {
    return productCard.querySelector('.product-card__meta');
  }

  function swapImage(productCard, newSrc) {
    var img = getFirstImg(productCard);
    if (!img) return;
    // Store original only once per hover session
    if (!productCard._amoonicOriginalSrc) {
      productCard._amoonicOriginalSrc = img.src;
      productCard._amoonicOriginalSrcset = img.srcset;
    }
    img.style.transition = 'opacity 0.15s ease';
    img.style.opacity = '0';
    img.srcset = '';
    img.src = newSrc;
    img.onload = function() { img.style.opacity = '1'; };
    // Fallback: if image is cached and onload doesn't fire
    setTimeout(function() { img.style.opacity = '1'; }, 300);
  }

  function restoreImage(productCard) {
    if (!productCard._amoonicOriginalSrc) return;
    var img = getFirstImg(productCard);
    if (!img) return;
    img.style.transition = 'opacity 0.15s ease';
    img.style.opacity = '0';
    img.src = productCard._amoonicOriginalSrc;
    img.srcset = productCard._amoonicOriginalSrcset || '';
    productCard._amoonicOriginalSrc = null;
    productCard._amoonicOriginalSrcset = null;
    img.onload = function() { img.style.opacity = '1'; };
    setTimeout(function() { img.style.opacity = '1'; }, 300);
  }

  function updateMaterialLabel(productCard, label) {
    var materialLabel = getMaterialLabel(productCard);
    if (!materialLabel) return;
    if (!productCard._amoonicOriginalMaterialLabel) {
      productCard._amoonicOriginalMaterialLabel = materialLabel.textContent;
    }

    if (label && label.trim()) {
      materialLabel.textContent = label.trim();
      materialLabel.hidden = false;
    } else {
      materialLabel.textContent = productCard._amoonicOriginalMaterialLabel || '';
      materialLabel.hidden = !materialLabel.textContent.trim();
    }
  }

  function restoreMaterialLabel(productCard) {
    var materialLabel = getMaterialLabel(productCard);
    if (!materialLabel) return;
    if (productCard._amoonicOriginalMaterialLabel !== undefined) {
      materialLabel.textContent = productCard._amoonicOriginalMaterialLabel || '';
      materialLabel.hidden = !materialLabel.textContent.trim();
    }
  }

  function setActiveSwatch(container, hoveredSwatch) {
    container.querySelectorAll('.card-metal-swatches__swatch, .card-stone-swatches__swatch').forEach(function(s) {
      s.classList.toggle('is-active', s === hoveredSwatch);
    });
  }

  function restoreActiveSwatch(container, productCard) {
    var originalUrl = productCard._amoonicOriginalActiveUrl;
    // mouseleave feuert zweimal (Button + Container). Beim zweiten Lauf ist
    // die Erinnerung schon geleert - ohne Guard wuerde dann gegen null
    // verglichen und der Aktiv-Ring von allen Swatches entfernt.
    if (!originalUrl) return;
    container.querySelectorAll('.card-metal-swatches__swatch, .card-stone-swatches__swatch').forEach(function(s) {
      s.classList.toggle('is-active', s.dataset.productUrl === originalUrl);
    });
  }

  // Mouseenter on a swatch: preview image + update active state
  document.addEventListener('mouseenter', function(e) {
    if (!e.target || !e.target.closest) return;
    var swatch = e.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;
    var productCard = getProductCard(swatch);
    if (!productCard) return;
    var container = swatch.closest(CONTAINER_SELECTOR);

    // Remember which swatch was originally active
    if (!productCard._amoonicOriginalActiveUrl) {
      var activeSwatch = container.querySelector('.card-metal-swatches__swatch.is-active, .card-stone-swatches__swatch.is-active');
      productCard._amoonicOriginalActiveUrl = activeSwatch ? activeSwatch.dataset.productUrl : null;
    }

    var newSrc = swatch.dataset.productImage;
    if (newSrc) swapImage(productCard, newSrc);
    updateMaterialLabel(productCard, swatch.dataset.productMaterial);
    setActiveSwatch(container, swatch);
  }, true);

  // Mouseleave on container: restore image + restore original active swatch
  document.addEventListener('mouseleave', function(e) {
    if (!e.target || !e.target.closest) return;
    var container = e.target.closest(CONTAINER_SELECTOR);
    if (!container) return;
    if (container.contains(e.relatedTarget)) return;
    var productCard = getProductCard(container);
    if (!productCard) return;

    restoreImage(productCard);
    restoreMaterialLabel(productCard);
    restoreActiveSwatch(container, productCard);
    productCard._amoonicOriginalActiveUrl = null;
  }, true);

  // Click: on touch devices (no real hover), just preview like mouseenter
  // would and point the card's own link at the selected variant - only
  // tapping the card itself should navigate. On devices with real hover,
  // the preview already happened on mouseenter, so click still navigates
  // straight to the variant.
  document.addEventListener('click', function(e) {
    if (!e.target || !e.target.closest) return;
    var swatch = e.target.closest(SWATCH_SELECTOR);
    if (!swatch) return;
    e.preventDefault();
    e.stopPropagation();

    var isTouch = window.matchMedia && window.matchMedia('(hover: none)').matches;
    var newUrl = swatch.dataset.productUrl;

    if (isTouch) {
      var productCard = getProductCard(swatch);
      if (!productCard) return;
      var container = swatch.closest(CONTAINER_SELECTOR);

      var newSrc = swatch.dataset.productImage;
      if (newSrc) swapImage(productCard, newSrc);
      updateMaterialLabel(productCard, swatch.dataset.productMaterial);
      setActiveSwatch(container, swatch);

      // Lock the preview in as the new baseline so a later, unrelated
      // mouseleave (some touch browsers still fire synthetic mouse
      // events) can't revert it before the shopper taps the card.
      productCard._amoonicOriginalSrc = null;
      productCard._amoonicOriginalSrcset = null;
      productCard._amoonicOriginalMaterialLabel = undefined;
      productCard._amoonicOriginalActiveUrl = null;

      if (newUrl) {
        productCard.querySelectorAll('[data-product-link]').forEach(function(link) {
          link.href = newUrl;
        });
      }
      return;
    }

    if (newUrl) { window.location.href = newUrl; }
  });
};

/**
 * Initialize all Amoonic custom functionality
 */
/**
 * EliteCart renders its own cart drawer inside an open shadow root and prints
 * line item properties as plain "Name: value" text. Turn upload URLs there into
 * "Name: <a>dateiname.jpg</a>", matching snippets/line-item.liquid.
 */
window.amoonic.initEliteCartPropertyLinks = function() {
  if (window.amoonic._eliteCartPropertyObserver || window.amoonic._eliteCartPropertyWaiting) return;

  var LINK_CLASS = 'amoonic-property-link';
  var URL_PATTERN = /https?:\/\/\S+/;

  function fileName(url) {
    var name = url.split(/[?#]/)[0].split('/').pop() || url;
    try { return decodeURIComponent(name); } catch (e) { return name; }
  }

  function linkifyProperty(element) {
    var textNode = Array.from(element.childNodes).find(function(node) {
      return node.nodeType === Node.TEXT_NODE && URL_PATTERN.test(node.textContent);
    });
    if (!textNode) return;

    // EliteCart re-renders by resetting the text node; drop our previous link first.
    var oldLink = element.querySelector('.' + LINK_CLASS);
    if (oldLink) oldLink.remove();

    var text = textNode.textContent;
    var url = text.match(URL_PATTERN)[0];
    var link = document.createElement('a');
    link.className = LINK_CLASS;
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = fileName(url);
    link.style.color = 'inherit';
    link.style.textDecoration = 'underline';

    textNode.textContent = text.slice(0, text.indexOf(url));
    element.insertBefore(link, textNode.nextSibling);
  }

  function processRoot(root) {
    root.querySelectorAll('.ec-lineitem-productproperties > div').forEach(linkifyProperty);
  }

  function attach() {
    var host = document.getElementById('elitecart-root');
    if (!host || !host.shadowRoot) return false;

    var root = host.shadowRoot;
    var scheduled = false;
    window.amoonic._eliteCartPropertyObserver = new MutationObserver(function() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function() {
        scheduled = false;
        processRoot(root);
      });
    });
    window.amoonic._eliteCartPropertyObserver.observe(root, { childList: true, subtree: true, characterData: true });
    processRoot(root);
    return true;
  }

  if (attach()) return;

  // The EliteCart app embed mounts after our script; wait for its root.
  var tries = 0;
  window.amoonic._eliteCartPropertyWaiting = true;
  var timer = setInterval(function() {
    tries += 1;
    if (attach() || tries > 60) {
      clearInterval(timer);
      window.amoonic._eliteCartPropertyWaiting = false;
    }
  }, 500);
};

/**
 * Desktop product images: click to zoom in place, the zoomed area follows the
 * mouse; click again or leave the image to zoom out. Only for the desktop
 * image grid and pointer devices that can hover; the expand icon still opens
 * the full-screen view. Images with the Cloudlift photo preview, videos and
 * 3D models are left alone.
 */
window.amoonic.initDesktopImageZoom = function() {
  if (window.amoonic._desktopImageZoomInitialized) return;
  window.amoonic._desktopImageZoomInitialized = true;

  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  var MIN_ZOOM = 2;
  var MAX_ZOOM = 3;

  var EXCLUDED = '.cl-po--preview, canvas, video, model-viewer, iframe';

  document.querySelectorAll('.main-product__media--grid-item .media').forEach(function(media) {
    var toggle = media.querySelector('a.product__gallery-toggle[data-pswp-image]');
    if (!toggle || !toggle.dataset.pswpSrc) return;
    if (media.querySelector(EXCLUDED)) return;

    // Cloudlift inserts its photo preview seconds after load: from then on
    // the image belongs to the preview (dragging the photo), not to the zoom
    function excluded() {
      if (!media.querySelector(EXCLUDED)) return false;
      media.classList.remove('amoonic-image-zoom', 'is-zoomed');
      media.style.backgroundImage = '';
      return true;
    }

    var zoomUrl = toggle.dataset.pswpSrc.replace(/([?&]width=)\d+/, '$12400');
    var zoomFactor = MIN_ZOOM;
    var preloaded = false;
    media.classList.add('amoonic-image-zoom');

    function preload() {
      if (preloaded) return;
      preloaded = true;
      var image = new Image();
      image.onload = function() {
        // Zoom up to the photo's own resolution, within MIN/MAX
        var ratio = image.naturalWidth / (media.offsetWidth || image.naturalWidth);
        zoomFactor = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, ratio));
        if (media.classList.contains('is-zoomed')) {
          media.style.backgroundSize = (zoomFactor * 100) + '%';
        }
      };
      image.src = zoomUrl;
    }

    function follow(event) {
      var rect = media.getBoundingClientRect();
      var x = Math.min(100, Math.max(0, (event.clientX - rect.left) / rect.width * 100));
      var y = Math.min(100, Math.max(0, (event.clientY - rect.top) / rect.height * 100));
      media.style.backgroundPosition = x + '% ' + y + '%';
    }

    function zoomIn(event) {
      preload();
      media.style.backgroundImage = 'url("' + zoomUrl + '")';
      media.style.backgroundSize = (zoomFactor * 100) + '%';
      follow(event);
      media.classList.add('is-zoomed');
    }

    function zoomOut() {
      media.classList.remove('is-zoomed');
    }

    media.addEventListener('mouseenter', function() {
      if (canHover.matches && !excluded()) preload();
    });

    media.addEventListener('click', function(event) {
      if (!canHover.matches || excluded()) return;
      if (event.target.closest('.product__gallery-opener, .media__overlay')) return;
      event.preventDefault();
      if (media.classList.contains('is-zoomed')) {
        zoomOut();
      } else {
        zoomIn(event);
      }
    });

    media.addEventListener('mousemove', function(event) {
      if (media.classList.contains('is-zoomed') && !excluded()) follow(event);
    });

    // Hiding the overlay under the pointer fires a stray mouseleave; only a
    // pointer that really is outside the image ends the zoom
    media.addEventListener('mouseleave', function(event) {
      var rect = media.getBoundingClientRect();
      var inside = event.clientX > rect.left && event.clientX < rect.right &&
        event.clientY > rect.top && event.clientY < rect.bottom;
      if (!inside) zoomOut();
    });
  });
};

window.amoonic.init = function() {
  window.amoonic.restoreConfPosition();
  window.amoonic.initConfigurator();
  window.amoonic.initSwatchOverflow();
  window.amoonic.initConfMerge();
  window.amoonic.initEngravingOptions();
  window.amoonic.initSizeTracking();
  window.amoonic.initEngravingSectionVisibility();
  window.amoonic.initDeliveryTime();
  window.amoonic.initDesktopImageZoom();
  window.amoonic.initMedaillonPreview();
  window.amoonic.initPreviewCanvasScroll();
  window.amoonic.watchForImageUploads();
  window.amoonic.initMedaillonUploadGroup();
  window.amoonic.initMedaillonUploadValidation();
  window.amoonic.initFeatureSectionDividers();
  window.amoonic.initPackagingPopoverCards();
  window.amoonic.initCardMetalSwatches();
  window.amoonic.initEliteCartPropertyLinks();
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.amoonic.init);
} else {
  window.amoonic.init();
}

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', window.amoonic.init);

// Legacy global variable for size tracking (compatibility with old theme)
Object.defineProperty(window, 'amo_global_size', {
  configurable: true,
  get: function() { return window.amoonic.globalSize; },
  set: function(val) { window.amoonic.globalSize = val; }
});
